import { Link, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, MessageCircle, PackageSearch, RefreshCcw } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { getServiceInstanceIdFromSearch, withServiceInstanceId } from '@/lib/service-instance'
import { metaApi, type MetaCatalogMapping } from '@/services/meta'
import { useAuthStore } from '@/stores/auth-store'

const isCataloged = (mapping: MetaCatalogMapping) => {
  const status = String(mapping.sync_status || '').toLowerCase()
  return Boolean(mapping.meta_item_id) || status === 'success' || status === 'skipped'
}

const payloadValue = (mapping: MetaCatalogMapping, key: string) => {
  const value = mapping.last_payload?.[key]
  return value == null ? '' : String(value)
}

const formatSyncTime = (value?: string | null) => {
  if (!value) {
    return 'Not synced yet'
  }
  return new Date(value).toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export default function WhatsAppCatalogPage() {
  const location = useLocation()
  const authServiceInstanceId = useAuthStore((state) => state.serviceInstanceId)
  const serviceInstanceId = getServiceInstanceIdFromSearch(location.search) || authServiceInstanceId

  const catalogQuery = useQuery({
    queryKey: ['meta-catalog-items', serviceInstanceId],
    queryFn: () => metaApi.listCatalogItems(serviceInstanceId!),
    enabled: Boolean(serviceInstanceId),
  })

  if (!serviceInstanceId) {
    return (
      <Card className="rounded-[24px]">
        <CardHeader>
          <CardTitle>WhatsApp Catalog</CardTitle>
          <CardDescription>Select a branch before viewing WhatsApp catalog packages.</CardDescription>
        </CardHeader>
      </Card>
    )
  }

  const catalogedPackages = (catalogQuery.data ?? []).filter(isCataloged)

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700">
            <MessageCircle className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">WhatsApp Catalog</h1>
            <p className="text-sm text-muted-foreground">Packages currently listed in this branch&apos;s WhatsApp catalog.</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" asChild>
            <Link to={withServiceInstanceId('/services/whatsapp', serviceInstanceId)}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Messages
            </Link>
          </Button>
          <Button variant="outline" onClick={() => catalogQuery.refetch()} disabled={catalogQuery.isFetching}>
            <RefreshCcw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
        </div>
      </div>

      <Card className="rounded-[24px] border-border/80">
        <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <CardTitle>Cataloged Packages</CardTitle>
            <CardDescription>These packages are linked to Meta catalog items and can be selected by customers in WhatsApp.</CardDescription>
          </div>
          <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-700">
            {catalogedPackages.length} packages
          </Badge>
        </CardHeader>
        <CardContent>
          {catalogQuery.isLoading ? (
            <div className="space-y-3">
              <Skeleton className="h-12 rounded-xl" />
              <Skeleton className="h-12 rounded-xl" />
              <Skeleton className="h-12 rounded-xl" />
            </div>
          ) : catalogedPackages.length === 0 ? (
            <div className="flex min-h-48 flex-col items-center justify-center gap-2 rounded-2xl border border-dashed p-8 text-center">
              <PackageSearch className="h-8 w-8 text-muted-foreground" />
              <p className="font-medium text-foreground">No WhatsApp catalog packages yet</p>
              <p className="text-sm text-muted-foreground">Use Add to WhatsApp from a package row to list it here.</p>
              <Button asChild variant="outline" className="mt-2">
                <Link to={withServiceInstanceId('/services/product-variants', serviceInstanceId)}>
                  Open Packages
                </Link>
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table className="min-w-[900px]">
                <TableHeader>
                  <TableRow>
                    <TableHead>Package</TableHead>
                    <TableHead>Service</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Price</TableHead>
                    <TableHead>WhatsApp Code</TableHead>
                    <TableHead>Last Synced</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {catalogedPackages.map((mapping) => (
                    <TableRow key={String(mapping.id)}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          {payloadValue(mapping, 'image_url') ? (
                            <img
                              src={payloadValue(mapping, 'image_url')}
                              alt=""
                              className="h-11 w-11 rounded-xl object-cover"
                            />
                          ) : (
                            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-muted text-muted-foreground">
                              <PackageSearch className="h-4 w-4" />
                            </div>
                          )}
                          <div>
                            <p className="font-medium text-foreground">
                              {mapping.product_variant_label || payloadValue(mapping, 'name') || 'Package'}
                            </p>
                            <p className="text-xs text-muted-foreground">{payloadValue(mapping, 'description')}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>{mapping.product_name || '-'}</TableCell>
                      <TableCell>{payloadValue(mapping, 'product_type') || '-'}</TableCell>
                      <TableCell>
                        {[payloadValue(mapping, 'currency'), payloadValue(mapping, 'price')].filter(Boolean).join(' ') || '-'}
                      </TableCell>
                      <TableCell>
                        <code className="rounded bg-muted px-2 py-1 text-xs">{mapping.retailer_id || '-'}</code>
                      </TableCell>
                      <TableCell>{formatSyncTime(mapping.last_synced_at || mapping.updated_on)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
